(function () {
  'use strict';

  const gsap = window.gsap;
  const CustomEase = window.CustomEase;
  const { layout } = window.Universe;
  let BestChoice; // lazy - set in init(), defined by universe-best-choices.js (section script)

  class ProductComparisonCard {
    constructor(card) {
      this.init(card);
    }

    onActiveIndexChange(newIndex) {
      BestChoice._waitForIndex = true;

      if (this.actualIndex === 1 && !BestChoice._reverseAnim) this.#playBack();
      else if (this.actualIndex === this.cardsCount && BestChoice._reverseAnim) this.#reverseBack();

      this.#updateActualIndex(newIndex);

      if (this.actualIndex < this.cardsCount && !BestChoice._reverseAnim) this.#playFront();
      else if (this.actualIndex > 1 && BestChoice._reverseAnim) this.#reverseFront();
    }

    #updateActualIndex(newIndex) {
      if (BestChoice._reverseAnim) this.actualIndex++;
      else this.actualIndex = this.index - newIndex + 1;

      if (this.actualIndex < 1) this.actualIndex += this.cardsCount;
      if (this.actualIndex > this.cardsCount) this.actualIndex -= this.cardsCount;
    }

    #getInitialY(index) {
      const i = index || this.index;
      return -i * 30 + 50;
    }

    #getInitialScale(index) {
      const i = index || this.index;
      return 1 - (i - 1) * 0.06;
    }

    #playBack() {
      this.DOM.card.style.pointerEvents = 'none';

      gsap
        .timeline({
          defaults: {
            ease: 'power3.inOut',
          },
        })
        .to(this.DOM.card, { y: 560, ease: 'power1', duration: 0.9 }, 0)
        .set(this.DOM.parent, { zIndex: 1 }, 0.65)
        .to(
          this.DOM.card,
          {
            scale: this.#getInitialScale(this.cardsCount),
            y: this.#getInitialY(this.cardsCount),
            duration: 0.7,
          },
          0.75
        )
        .call(() => { if (this.tl) this.tl.restart().pause(); }, undefined, 1.25)
        .then(() => { this.DOM.card.style.pointerEvents = 'auto'; });
    }

    #playFront() {
      if (this.actualIndex === 1 && this.tl) this.tl.play();

      const delay = this.actualIndex === 2 ? 0.25 : 0;

      gsap
        .timeline({
          delay: delay,
          defaults: {
            ease: 'power1.inOut',
            duration: 1.25,
          },
        })
        .to(
          this.DOM.card,
          {
            y: this.#getInitialY(this.actualIndex),
            ease: CustomEase.create(
              'custom',
              'M0,0 C0.016,0.058 0.211,-0.028 0.281,-0.028 0.535,-0.028 0.507,0.699 0.666,0.915 0.715,0.982 0.925,1 1,1'
            ),
          },
          0
        )
        .to(
          this.DOM.card,
          {
            scale: this.#getInitialScale(this.actualIndex),
          },
          0
        )
        .set(this.DOM.parent, { zIndex: this.cardsCount - this.actualIndex + 1 }, 0.85)
        .then(() => { BestChoice._waitForIndex = false; });
    }

    #reverseBack() {
      this.DOM.card.style.pointerEvents = 'none';

      gsap
        .timeline({
          defaults: {
            ease: 'power3.inOut',
            duration: 1,
          },
        })
        .to(this.DOM.card, { y: 540, ease: 'power1.inOut' }, 0)
        .to(this.DOM.card, { scale: this.#getInitialScale(1), y: this.#getInitialY(1) }, 0.75)
        .set(this.DOM.parent, { zIndex: this.cardsCount }, 1)
        .call(
          () => {
            if (this.actualIndex === 1 && this.tl) this.tl.play();
          },
          undefined,
          1
        )
        .then(() => { this.DOM.card.style.pointerEvents = 'auto'; });
    }

    #reverseFront() {
      if (this.tl) this.tl.reverse();

      const delay = this.actualIndex === 2 ? 0.25 : 0;

      gsap
        .timeline({
          delay: delay,
          defaults: {
            ease: 'power1.inOut',
            duration: 0.75,
          },
        })
        .to(this.DOM.card, {
          y: this.#getInitialY(this.actualIndex),
          scale: this.#getInitialScale(this.actualIndex),
        })
        .set(this.DOM.parent, { zIndex: this.cardsCount - this.actualIndex + 1 }, 0.5)
        .then(() => {
          BestChoice._waitForIndex = false;
          BestChoice._reverseAnim = false;
        });
    }

    initCardsAnimations() {
      const ctx = gsap.context(() => {
        this.tl = gsap
          .timeline({
            paused: true,
            defaults: {
              ease: 'power3',
              duration: 1.25,
            },
          })
          .to('.js-anims-info', { y: 0, yPercent: 0 }, 0)
          .to('.js-anim-image-mobile', { autoAlpha: 1 }, 0)
          .to('.js-anims-feature-border', { scaleX: 1 }, 0.25)
          .to('.js-anims-feature-text', { y: 0, yPercent: 0 }, 0.25)
          .to('.js-anims-feature-arrow', { x: 0, xPercent: 0 }, 0.25)
          .to('.js-anims-button', { y: 0, yPercent: 0 }, 0.25);
      }, this.DOM.card);

      this.gsapContexts.push({ id: 'mobile', ctx: ctx });
    }

    animateMobile() {
      this.index = Number(this.DOM.dataset.index);
      this.actualIndex = this.index;

      this.initCardsAnimations();
    }

    animateDesktop() {
      const mainCtx = gsap.context(() => {
        const animationType = this.DOM.dataset.animation;
        const borderRadius = getComputedStyle(document.documentElement).getPropertyValue('--global-border-radius-medium').trim() || '20px';

        const hoverTl = gsap
          .timeline({
            paused: true,
            defaults: {
              ease: 'power3.inOut',
              duration: 0.75,
            },
          })
          .to(
            '.js-anim-image-wrapper',
            {
              width: '100%',
              height: '100%',
              yPercent: -10,
              duration: 0.75,
              scale: 1.3,
              clipPath: `rect(-50% 150% 200% -50% round ${borderRadius})`,
              startAt: { clipPath: `rect(0% 100% 100% 0% round ${borderRadius})` },
            },
            0
          )
          .to('.js-anims-features', { yPercent: -30, duration: 0.75 }, 0);

        if (animationType == 'simple') {
          hoverTl.to('.js-anims-info-relative', { autoAlpha: 0.3 }, 0);
        } else {
          hoverTl.to('.js-anims-info-absolute', { y: 0, yPercent: 0 }, 0);
          hoverTl.to('.js-anims-info-relative', { yPercent: -100 }, 0);
          hoverTl.to('.js-anims-best', { yPercent: -300, duration: 0.5 }, 0);
        }

        hoverTl.to('.js-anims-button', { opacity: 1, y: -35, ease: 'power4' }, 0.3);

        const handleMouseEnter = () => { hoverTl.play(); };
        const handleMouseLeave = () => { hoverTl.reverse(); };
        const handleFocusIn = () => { hoverTl.play(); };
        const handleFocusOut = () => { hoverTl.reverse(); };

        this.DOM.card.addEventListener('mouseenter', handleMouseEnter);
        this.DOM.card.addEventListener('mouseleave', handleMouseLeave);
        this.DOM.card.addEventListener('focusin', handleFocusIn);
        this.DOM.card.addEventListener('focusout', handleFocusOut);

        return () => {
          this.DOM.card.removeEventListener('mouseenter', handleMouseEnter);
          this.DOM.card.removeEventListener('mouseleave', handleMouseLeave);
          this.DOM.card.removeEventListener('focusin', handleFocusIn);
          this.DOM.card.removeEventListener('focusout', handleFocusOut);
        };
      }, this.DOM.card);

      this.gsapContexts.push({ id: 'main', ctx: mainCtx });
    }

    animate() {
      this.gsapContexts = [];

      if (layout.window.width < 1024) this.animateMobile();
      else this.animateDesktop();
    }

    setup() {
      if (this.DOM.card) this.animate();
    }

    reinit() {
      this.destroy();
      this.setup();
    }

    init(container) {
      try {
        if (!BestChoice) BestChoice = window.Universe.BestChoice;

        this.DOM = {};

        this.DOM.card = container;

        this.DOM.dataset = container.dataset;
        this.DOM.parent = container.parentElement;
        this.DOM.features = container.querySelectorAll('.js-comparison-feature');

        this.cardsCount = BestChoice._cardsCount;

        BestChoice.registerListener((newValue) => { this.onActiveIndexChange(newValue); });

        this.setup();
      } catch (error) {
        console.log(error);
      }
    }

    destroy() {
      if (this.DOM) {
        this.gsapContexts.forEach((item) => { item.ctx.revert(); });
      }
    }
  }

  window.Universe.ProductComparisonCard = ProductComparisonCard;
})();
